import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft } from "lucide-react";
import api from "../api";
import SEO from "../components/SEO";

const SITE_URL = "https://prosourcelabs.org";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  // 'unknown' → initial state, no meta decisions made yet
  // 'loaded'  → api returned an article successfully
  // 'not_found' → api returned 404 (slug genuinely doesn't exist)
  // 'error' → any other failure (timeout/network/5xx) — DO NOT noindex
  //           because these are transient and would poison Google's index
  //           for legitimate articles during a rendering-pass hiccup.
  const [state, setState] = useState("unknown");

  useEffect(() => {
    let cancelled = false;

    async function fetchWithRetry(tries = 2) {
      for (let attempt = 1; attempt <= tries; attempt++) {
        try {
          const { data } = await api.get(`/blog/posts/${slug}`);
          if (!cancelled) {
            setPost(data);
            setState("loaded");
          }
          return;
        } catch (err) {
          const status = err?.response?.status;
          if (status === 404) {
            // Definitive "this slug does not exist" answer from the server.
            // Safe to noindex — no chance of a false positive.
            if (!cancelled) setState("not_found");
            return;
          }
          // Transient error (network, timeout, 5xx). Retry once, then
          // fall through to a NON-noindex error state so Google's
          // rendering-pass hiccups don't strip legitimate articles from
          // the index. This was the root cause of GSC's "Excluded by
          // 'noindex' tag" report on 11 valid /research-library/* URLs.
          if (attempt < tries) {
            await new Promise((r) => setTimeout(r, 500 * attempt));
            continue;
          }
          if (!cancelled) setState("error");
        }
      }
    }

    setState("unknown");
    setPost(null);
    fetchWithRetry();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state === "not_found") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <SEO title="Not Found" path={`/research-library/${slug}`} noindex />
        <div className="overline">404 · ARTICLE NOT FOUND</div>
        <h1 className="font-heading text-3xl mt-3 mb-4">Looks like this article moved.</h1>
        <Link to="/research-library" className="text-[#00C3FF] hover:underline text-sm font-mono-tech uppercase tracking-wider">
          ← Back to Research Library
        </Link>
      </div>
    );
  }

  if (state === "error") {
    // Transient error — show a soft "please try again" message but DO NOT
    // apply noindex. The static per-route HTML shipped by
    // generate-static-routes.js already carries the correct title +
    // canonical + og tags + `index, follow` robots directive; leaving them
    // in place is the correct call for a temporary failure.
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="blog-load-error">
        <div className="overline">TEMPORARILY UNAVAILABLE</div>
        <h1 className="font-heading text-3xl mt-3 mb-4">This article couldn&apos;t load right now.</h1>
        <p className="text-sm text-[#8A94A6] mb-6">Please refresh the page in a moment.</p>
        <Link to="/research-library" className="text-[#00C3FF] hover:underline text-sm font-mono-tech uppercase tracking-wider">
          ← Back to Research Library
        </Link>
      </div>
    );
  }

  if (!post) return <div className="max-w-3xl mx-auto px-6 py-20 text-[#8A94A6]">Loading…</div>;

  // Build a clean plain-text description for <meta name="description">.
  // The `excerpt` field is unreliable — many articles have their leading
  // research-use disclaimer as excerpt, which makes for a terrible SERP
  // preview. Prefer `meta_description`, fall back to the first meaningful
  // paragraph of the body (after stripping markdown syntax and skipping
  // the disclaimer line), and finally fall back to the excerpt.
  const cleanDescription = (() => {
    if (post.meta_description) return post.meta_description;

    const stripMd = (s) =>
      (s || "")
        .replace(/^#{1,6}\s+/gm, "")            // heading markers
        .replace(/\*\*(.+?)\*\*/g, "$1")        // bold
        .replace(/\*(.+?)\*/g, "$1")             // italics
        .replace(/_([^_]+)_/g, "$1")             // underscore italics
        .replace(/`([^`]+)`/g, "$1")             // inline code
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // links
        .replace(/^>\s+/gm, "")                  // blockquotes
        .replace(/^-{3,}\s*$/gm, "")             // horizontal rules
        .replace(/[\r\n]+/g, " ")                // newlines → spaces
        .replace(/\s+/g, " ")                    // collapse whitespace
        .trim();

    const disclaimerRe = /for research purposes only|not for human consumption/i;
    // Split body into paragraph-ish chunks, find the first one that isn't
    // a research-use disclaimer and isn't too short to be meaningful.
    const chunks = (post.body_markdown || "")
      .split(/\n{2,}/)
      .map(stripMd)
      .filter((c) => c && !disclaimerRe.test(c) && c.length >= 60);
    if (chunks.length) return chunks[0].slice(0, 300);

    // Last resort — use whatever excerpt was set, minus markdown.
    return stripMd(post.excerpt || "").slice(0, 300);
  })();

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: cleanDescription,
    image: post.cover_image ? [post.cover_image] : undefined,
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: { "@type": "Organization", name: "ProSource Labs" },
    publisher: {
      "@type": "Organization",
      name: "ProSource Labs",
      logo: {
        "@type": "ImageObject",
        url: "https://customer-assets.emergentagent.com/job_peptide-pro-6/artifacts/geglxhs1_ChatGPT%20Image%20Apr%2028%2C%202026%2C%2001_46_32%20PM.png",
      },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/research-library/${slug}` },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Research Library", item: `${SITE_URL}/research-library` },
      { "@type": "ListItem", position: 3, name: post.title, item: `${SITE_URL}/research-library/${slug}` },
    ],
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <SEO
        title={post.title}
        description={cleanDescription}
        path={`/research-library/${slug}`}
        image={post.cover_image}
        type="article"
        keywords={(post.tags || []).join(", ")}
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />
      <Link to="/research-library" className="overline hover:text-white transition-colors flex items-center gap-2">
        <ArrowLeft size={13} /> BACK TO RESEARCH LIBRARY
      </Link>

      {/* Compliance disclaimer — top of every article */}
      <div
        className="mt-6 border border-yellow-400/30 bg-yellow-400/5 p-4 text-xs text-yellow-100/80 font-mono-tech leading-relaxed"
        data-testid="blog-compliance-banner"
      >
        <span className="uppercase tracking-[0.18em] text-yellow-300 mr-2">RESEARCH USE ONLY</span>
        This article discusses biochemistry, mechanism of action, and preclinical study findings only.
        Nothing herein constitutes medical advice, dosing guidance, or usage instructions.
        Products discussed are sold strictly for laboratory research — not for human or animal consumption.
      </div>

      {post.cover_image && (
        <img
          src={post.cover_image}
          alt={post.title}
          className="w-full aspect-video object-cover mt-6 border border-white/10"
          loading="lazy"
        />
      )}

      <div className="mt-8 text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-wider flex items-center gap-3 flex-wrap">
        {post.tags?.slice(0, 3).map((t) => (
          <span key={t} className="text-[#00C3FF]/80">#{t}</span>
        ))}
        {post.created_at && (
          <span>· {new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
        )}
      </div>

      {/* Article body — prose styling via tailwind + custom markdown component overrides */}
      <article
        className="mt-10 text-[#D5DBE5] leading-relaxed"
        data-testid="blog-post-body"
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ node, ...props }) => (
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-6 mt-4" {...props} />
            ),
            h2: ({ node, ...props }) => (
              <h2 className="font-heading text-2xl lg:text-3xl font-light text-white mt-12 mb-4" {...props} />
            ),
            h3: ({ node, ...props }) => (
              <h3 className="font-heading text-xl text-[#00C3FF] mt-8 mb-3" {...props} />
            ),
            p: ({ node, ...props }) => <p className="mb-4 text-base" {...props} />,
            ul: ({ node, ...props }) => <ul className="list-disc pl-6 mb-4 space-y-1" {...props} />,
            ol: ({ node, ...props }) => <ol className="list-decimal pl-6 mb-4 space-y-1" {...props} />,
            li: ({ node, ...props }) => <li className="text-base" {...props} />,
            a: ({ node, ...props }) => (
              <a className="text-[#00C3FF] hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
            ),
            blockquote: ({ node, ...props }) => (
              <blockquote className="border-l-2 border-[#00C3FF]/60 pl-4 italic text-[#8A94A6] my-6" {...props} />
            ),
            strong: ({ node, ...props }) => <strong className="text-white font-semibold" {...props} />,
            code: ({ node, ...props }) => (
              <code className="bg-white/5 px-1.5 py-0.5 text-[#00C3FF] font-mono text-sm" {...props} />
            ),
            hr: () => <hr className="border-white/10 my-10" />,
          }}
        >
          {post.body_markdown}
        </ReactMarkdown>
      </article>

      <div className="mt-16 pt-8 border-t border-white/10 text-[10px] font-mono-tech text-[#5C657A] uppercase tracking-[0.2em]">
        For research purposes only · Not for human consumption
      </div>

      <div className="mt-10 flex items-center justify-between text-sm">
        <Link to="/research-library" className="text-[#00C3FF] hover:underline font-mono-tech text-xs uppercase tracking-wider">
          ← MORE ARTICLES
        </Link>
        <Link to="/shop" className="text-[#00C3FF] hover:underline font-mono-tech text-xs uppercase tracking-wider">
          SHOP CATALOG →
        </Link>
      </div>
    </div>
  );
}
